import { useState } from 'react';
import { Helmet } from 'react-helmet-async';

import LoadingBox from '@/UI/LoadingBox';
import LoadingOpacity from '@/UI/LoadingOpacity';

import type { IInvoiceSaleFullCanEdit } from './api/saleToIncome.interface';
import ReportHeader from './components/FormHeader';
import { InitialScreen } from './components/IntialScreen';
import InvoiceHeader from './components/InvoiceHeader';
import ItemsTable from './components/ItemsTable';

function SalesInvoiceCovertInComePage() {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingConvert, setLoadingConvert] = useState<boolean>(false);
  const [invoiceData, setInvoiceData] =
    useState<IInvoiceSaleFullCanEdit | null>(null);

  return (
    <>
      <Helmet>
        <title> تحويل بيع طلبية وارد </title>
        <style>
          {`
            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(8px); }
              to { opacity: 1; transform: translateY(0); }
            }
            
            @keyframes pulseSlow {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.85; }
            }
            
            .animate-fade-in {
              animation: fadeIn 0.4s ease-out forwards;
            }
            
            .animate-pulse-slow {
              animation: pulseSlow 1.8s infinite;
            }

            @keyframes shimmer {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
          `}
        </style>
      </Helmet>
      <div className="relative flex min-h-full w-full flex-col overflow-hidden rounded-lg bg-gradient-to-br from-white via-purple-50/50 to-indigo-50/50 dark:from-gray-950 dark:via-purple-950/30 dark:to-indigo-950/30">
        {/* Background decorative elements */}
        <div className="absolute -left-28 -top-28 h-56 w-56 rounded-full bg-gradient-to-br from-purple-500/10 to-indigo-500/10 blur-2xl dark:from-purple-600/5 dark:to-indigo-600/5"></div>
        <div className="absolute -bottom-28 -right-28 h-56 w-56 rounded-full bg-gradient-to-br from-indigo-500/10 to-purple-500/10 blur-2xl dark:from-indigo-600/5 dark:to-purple-600/5"></div>

        {/* Pattern overlay */}
        <div
          className="pointer-events-none absolute inset-0 z-10 opacity-20 dark:opacity-15"
          style={{
            backgroundImage: `
              radial-gradient(#6d28d9 0.5px, transparent 0.5px), 
              radial-gradient(#6d28d9 0.5px, transparent 0.5px)
            `,
            backgroundSize: '20px 20px',
            backgroundPosition: '0 0, 10px 10px',
          }}
        />

        {/* Diagonal lines */}
        <div
          className="dark:opacity-3 pointer-events-none absolute inset-0 z-10 opacity-5"
          style={{
            backgroundImage: `
              linear-gradient(135deg, #6d28d9 0.5px, transparent 0.5px),
              linear-gradient(45deg, #6d28d9 0.5px, transparent 0.5px)
            `,
            backgroundSize: '60px 60px',
          }}
        />

        {/* Accent line at top */}
        <div className="h-0.5 bg-gradient-to-r from-purple-300/0 via-indigo-400/30 to-purple-300/0 dark:from-purple-700/0 dark:via-indigo-600/30 dark:to-purple-700/0"></div>

        <ReportHeader
          loading={loading}
          setInvoiceData={setInvoiceData}
          setLoading={setLoading}
        />

        <div className="relative mx-auto w-full max-w-[1700px] flex-1 p-3">
          {invoiceData && (
            <div className="grid animate-fade-in grid-cols-1 gap-3 lg:grid-cols-[1fr_2fr]">
              <div>
                <InvoiceHeader
                  invoiceHeader={{
                    ...invoiceData?.invoiceHeader,
                    BulkInvoice: false,
                    SpecialInvoice: false,
                  }}
                  oldData={invoiceData?.oldData}
                />
              </div>
              <div className="rounded-xl border border-gray-200/70 bg-white/90 p-0.5 shadow-md backdrop-blur-sm transition-all hover:shadow-lg dark:border-gray-800/50 dark:bg-black/80">
                <ItemsTable
                  Items={invoiceData?.invoiceItems}
                  clientID={invoiceData?.invoiceHeader?.Customer_ID}
                  salesID={invoiceData?.invoiceHeader?.ID}
                  setLoadingConvert={setLoadingConvert}
                  setInvoiceData={setInvoiceData}
                />
              </div>
            </div>
          )}

          {!invoiceData && !loading && (
            <div className="flex h-[calc(100vh-160px)] items-center justify-center p-4">
              <div className="w-full max-w-4xl animate-fade-in">
                <InitialScreen />
              </div>
            </div>
          )}

          {loading && (
            <div className="flex min-h-[calc(100vh-200px)] animate-pulse-slow flex-col items-center justify-center p-2">
              <LoadingBox />
            </div>
          )}

          {loadingConvert && <LoadingOpacity isOpen={loadingConvert} />}
        </div>

        {/* Accent line at bottom */}
        <div className="h-0.5 bg-gradient-to-r from-purple-300/0 via-indigo-400/30 to-purple-300/0 dark:from-purple-700/0 dark:via-indigo-600/30 dark:to-purple-700/0"></div>
      </div>
    </>
  );
}

export default SalesInvoiceCovertInComePage;
